import dayjs from 'dayjs';
import request from '../request';
import type {
  EmploymentInfo,
  ProjectOpportunity,
  ReviewKind,
  ReviewListItem,
  ReviewWorktime,
  SignedReviewFormValue,
  UnsignedReviewFormValue,
} from '@/routes/ithClient/projectReview/types';

/** 后端成员工时记录原始结构（GVA_MODEL 主键序列化为 ID） */
interface GrossProfitReviewWorktimeRaw {
  ID: number;
  clerkCode: string;
  name: string;
  majorName: string;
  companyName: string;
  departmentName: string;
  totalHours: number;
  workday: number;
}

/** 后端复盘记录原始结构（ProjectGrossProfitReview 序列化结果，GVA_MODEL 时间字段序列化为 CreatedAt） */
interface GrossProfitReviewRaw {
  uuid: string;
  isSigned: boolean;
  code: string;
  /** 复盘月份（YYYY-MM），历史数据可能为空，空时由 CreatedAt 派生 */
  reviewMonth?: string;
  projectName: string;
  departmentName: string;
  managerName: string;
  CreatedAt: string;
  /** 提交状态：列表接口返回 "" / draft / submitted（空串表示无保存内容） */
  submissionStatus?: string;
  /** 提交时间（仅 submitted 时有值） */
  submittedAt?: string | null;
}

/**
 * 后端复盘记录详情原始结构（ProjectGrossProfitReview 原样序列化）
 * @see workbench/router/projects/project.go GetProjectGrossProfitReviewByUUID
 */
export interface GrossProfitReviewDetailRaw extends GrossProfitReviewRaw {
  ID: number;
  uuid: string;
  opProjectID: number;
  opProjectName: string;
  opUserID: number;
  companyCode: string;
  companyName: string;
  departmentCode: string;
  managerCode: string;
  /** 预计毛利率（%，后台创建/导入时从 bi 库 PLM_PRJ_EST_MARGIN 固化，无匹配数据时为 null） */
  expectedProfitRate?: number | null;
}

/** 商机列表接口原始响应 */
export interface GrossProfitReviewBusinessOpportunitiesRespRaw {
  /** 项目编号获取的商机列表 */
  projectOpportunities?: ProjectOpportunity[] | null;
  /** 工程编号获取的商机列表 */
  projectOpportunitiesByProjectCode?: ProjectOpportunity[] | null;
}

/** 商机列表接口标准化响应（数组字段始终为数组） */
export interface GrossProfitReviewBusinessOpportunitiesResp {
  projectOpportunities: ProjectOpportunity[];
  projectOpportunitiesByProjectCode: ProjectOpportunity[];
}

/**
 * 后端 BI 毛利 KPI 原始结构（bi 库 PLM_PRJ_SHOW_KPI_SAVE 快照，按 savedate 倒序返回）
 * 各字段为截止 savedate 的累计值，因此「当月 / 当年累计」需用相邻快照做差得到
 * @see workbench/router/projects/project.go PlmPrjShowKpiSave / GetProjectGrossProfitReviewKpiSave
 */
export interface GrossProfitReviewKpiSaveRaw {
  saveDate: string;
  /** 指标口径 */
  indicator: string;
  projectItemCode: string;
  orgCode: string;
  deptCode: string;
  professionCode: string;
  /** 工时人力成本（元） */
  manHourLaborCost: number;
  /** 项目成本（元） */
  projectCost: number;
  /** 人力成本分包（元） */
  laborCostSubcontract: number;
  /** 公司费用分摊（元） */
  companyExpenseAllocation: number;
  /** 部门成本分摊（元） */
  deptCostAllocation: number;
  /** 税金分摊（元） */
  taxAllocation: number;
  /** 实收产值（元） */
  actualIncome: number;
  /** 开票额（元） */
  invoiceAmount: number;
  /** 工日数 */
  workDays: number;
  /** 工时数 */
  workHours: number;
  /** 完成产值（元） */
  completionOutputValue: number;
  /** 可收产值（元） */
  receivableOutputValue: number;
  /** 项目总成本（元） */
  projectTotalCost: number;
  /** 项目进度（%） */
  projectProgress: number;
}

/** 复盘绑定的毛利指标快照（创建/导入时固化） */
export interface GrossProfitReviewKpiMetricRaw {
  /** 指标 */
  indicator: string;
  /** 完成产值（元） */
  completionOutputValue: number;
  /** 完成产值毛利（元） */
  completionOutputProfit: number;
  /** 完成产值毛利率（%） */
  completionOutputProfitRate: number;
  /** 实收产值（元） */
  actualIncome: number;
  /** 实收毛利率（%） */
  actualProfitRate: number;
  /**
   * 工时人力成本（元）：按「指标」维度固化，
   * 当月 / 当年累计 / 累计 行分别对应当月投入 / 当年投入 / 累计投入人力成本。
   * 历史未重新固化的记录为 null（前端回退旧口径计算）
   */
  manHourLaborCost?: number | null;
}

/** kpi-saves 接口响应结构 */
export interface GrossProfitReviewKpiSavesResp {
  uuid: string;
  projectCode: string;
  opProjectID: number;
  opProjectName: string;
  /** 预计毛利率（%，后台创建/导入时从 bi 库 PLM_PRJ_EST_MARGIN 固化，无匹配数据时为 null） */
  expectedProfitRate?: number | null;
  /** 兼容旧口径：原始 BI 快照列表（按 savedate 倒序） */
  list: GrossProfitReviewKpiSaveRaw[];
  /** 新口径：绑定当前复盘的指标快照（按指标） */
  metricList?: GrossProfitReviewKpiMetricRaw[];
}

type GetProjectsParams = API.PaginationParams<{
  keywords?: string;
  projectName?: string;
  projectCode?: string;
}>;
/**
 * 获取项目列表
 */
export const getProjects = async (params: GetProjectsParams): Promise<API.ListData<API.PROJECT.ListItem>> => {
  return await request.get('/workbenchApi/projects', {
    params
  });
};

/**
 * 获取项目详情
 */
export const getProject = async (code: string): Promise<API.PROJECT.Detail> => {
  return await request.get(`/workbenchApi/projects/${code}`);
};
/**
 * 获取项目合同列表
 * @param projectID op项目ID
 * @returns 
 */
export const getProjectContracts = (projectID: number): Promise<API.PROJECT.Contract[]>=>{
  return request.get(`/workbenchApi/projects/${projectID}/contracts`);
}
/**
 * 获取项目菜单权限
 * @param projectID op项目ID
 * @returns 
 */
export const getProjectPermissionsApi = (projectID: number): Promise<{ permissions: string[] }> => {
  return request.get(`/workbenchApi/op/projects/${projectID}/permissions`);
}
/**
 * 获取项目预估产值
 * @param projectID op项目ID
 * @returns 
 */
export const getProjectEstimatedsApi = (projectID: number | string): Promise<{ value: number, isPm: boolean}> => {
  return request.get(`/workbenchApi/op/projects/${projectID}/estimateds`);
}
/**
 * 修改项目预估产值
 * @param projectID op项目ID
 * @returns 
 */
export const updateProjectEstimatedsApi = (projectID: number, estimatedAmount: number): Promise<any> => {
  return request.post(`/workbenchApi/op/projects/${projectID}/estimateds`, {
    estimatedAmount
  });
}


/**
 * 获取项目标准里程碑
 * @param id 项目ID
 * @returns 
 */
export const getProjectMilestonesApi = (id: number | string, plotID?: number, invoiceToken?: string): Promise<API.PROJECT.Milestone[]> => {
  let url = `/workbenchApi/projects/${id}/standard-milestones`
  if (invoiceToken) {
    url += `?_token=${invoiceToken}`
  }
  if (plotID) {
    return request.get(url, {
      params: {
        plotID,
        invoiceToken
      }
    });
  }
  return request.get(url);
}

/**
 * 获取项目标准里程碑
 * @param id 项目ID
 * @returns 
 */
export const saveProjectMilestonesApi = (id: number | string,
  data: { status: number, milestoneCode: string, milestoneName: string, standardDate: string, actualDate?: string, confirmationAttachs?: API.PROJECT.MilestoneAttachment[], isAdvanceInvoice?: boolean }[],
  plotID?: number,
  invoiceToken?: string,
  circleCompletion?: { status: number, milestoneCode: string, milestoneName: string, standardDate: string, actualDate?: string }): Promise<API.PROJECT.Milestone[]> => {
  let url = `/workbenchApi/projects/${id}/standard-milestones/date`
  if (invoiceToken) {
    url += `?_token=${invoiceToken}`
  }
  return request.post(url, {
    dates: data,
    plotID: plotID,
    circleCompletion: circleCompletion
  });
}
export const createProjectMilestonePlotApi = (id: number | string, data: Req.ProjectPlot[], invoiceToken?: string): Promise<API.PROJECT.Milestone[]> => {
  let url = `/workbenchApi/projects/${id}/standard-milestones/plots`
  if (invoiceToken) {
    url += `?_token=${invoiceToken}`
  }
  return request.post(url, {
    plots: data
  });
}

/**
 * 获取项目标准里程碑编辑历史
 * @param id 项目ID
 * @returns 
 */
export const getMilestoneHistoryApi = (id: number | string, code: string, plotID?: number): Promise<API.PROJECT.MilestoneHistory[]> => {
  return request.get(`/workbenchApi/projects/${id}/standard-milestones/${code}/history`, {
    params: {
      plotID
    }
  });
}

/**
 * 标准里程碑批量编辑列表
 * @returns 
 */
export const getProjectEditMilestonesApi = (params: Req.MilestoneListParams): Promise<API.ListData<API.PROJECT.EditMilestone>> => {
  return request.get(`/workbenchApi/projects/edit-standard-milestones`, {
    params
  });
}

/**
 * 标准里程碑列表编辑状态
 * @returns 
 */
export const saveProjectEditMilestoneApi = (id: number, params: Partial<Request.EditMilestoneItem>): Promise<any> => {
  return request.patch(`/workbenchApi/projects/${id}/standard-milestones/${params.code}/date`, params);
}
/**
 * 标准里程碑所有项目状态
 * @returns 
 */
export const getProjectMilestoneStatusApi = (): Promise<API.PROJECT.Status[]> => {
  return request.get(`/workbenchApi/projects/edit-standard-milestones/status`);
}
/**
 * 编辑项目状态
 * @returns 
 */
export const updateProjectStatusApi = (projectID: number, code: string, plotID?: number): Promise<API.PROJECT.Status[]> => {
  return request.patch(`/workbenchApi/projects/${projectID}/status`, {
    code,
    plotID
  });
}

/**
 * 获取pm公司
 * 
 */
export const getPMCompaniesApi = (): Promise<API.PROJECT.CompanyDept[]> => {
  return request.get(`/workbenchApi/pm/companies`);
}
/**
 * 获取pm项目阶段
 * 
 */
export const getPMStagesApi = (): Promise<API.PROJECT.TypeStage[]> => {
  return request.get(`/workbenchApi/pm/bigStages`);
}
/**
 * 获取pm项目
 * 
 */
export const getPMProjectsApi = (params: Req.ProjectParams): Promise<API.ListData<API.PROJECT.Info>> => {
  return request.get(`/workbenchApi/pm/projects`, {
    params
  });
}
export const followPMProjectApi = (code: string): Promise<any> => {
  return request.post(`/workbenchApi/pm/follow-project`, {
    code
  });
}

export const getOverviewProjectsApi = (projectId: string): Promise<API.PROJECT.OverviewProject[]> => {
  return request.get(`/workbenchApi/overviews/projects/${projectId}/all-projects`);
}
export const getOverviewMilestonesApi = (params: { projectIds: number[], startTime: string, endTime: string }): Promise<API.PROJECT.OverviewMilestone[]> => {
  return request.get(`/workbenchApi/overviews/projects/milestones`, {
    params
  });
}
export const saveOverviewConfigApi = (projectId: string, data: API.PROJECT.OverviewConfig): Promise<API.PROJECT.OverviewConfig> => {
  return request.post(`/workbenchApi/overviews/projects/${projectId}/selected-projects`, data);
}
export const getOverviewConfigApi = (projectId: string): Promise<API.PROJECT.OverviewConfig[]> => {
  return request.get(`/workbenchApi/overviews/projects/${projectId}/selected-projects`);
}
export const getOverviewProjectInfoApi = (projectId: string | number): Promise<API.PROJECT.OverviewProjectData> => {
  return request.get(`/workbenchApi/overviews/projects/${projectId}/info`);
}
export const getPlmProjectAllTeamUserApi = (projectId: string | number): Promise<{id: number, name: string}[]> => {
  return request.get(`/workbenchApi/overviews/projects/${projectId}/all-members`)
}
// 获取plm的标准任务
export const getPlmProjectStandardsApi = (projectId: string | number): Promise<{ id: number, name: string }[]> => {
  return request.get(`/workbenchApi/projects/${projectId}/standards`)
}
export const getPlmProjectAllStatusApi = (projectId: string | number): Promise<Plm.WorkpackageStatus[]> => {
  return request.get(`/workbenchApi/projects/${projectId}/status`)
}
export const createPlmProjectMilestoneApi = (projectId: string | number, data: Request.PlmMilestone): Promise<API.PROJECT.OverviewProjectData> => {
  return request.post(`/workbenchApi/projects/${projectId}/milestones`, data)
}
export const getPlmOverbudgetProjectsApi = (): Promise<API.PROJECT.OverviewProject[]> => {
  return request.get(`/workbenchApi/overviews/projects/budget-overrun`)
}

/**
 * 获取当前登录用户（管理所长）的待复盘项目列表
 * 后端已按 created_at 倒序返回；前端据此映射为列表项，复盘发送月份优先取 reviewMonth 字段（历史数据由 CreatedAt 派生）
 */
export const getProjectGrossProfitReviewsApi = async (): Promise<ReviewListItem[]> => {
  const data: GrossProfitReviewRaw[] = await request.get(
    '/workbenchApi/projects/project-gross-profit-reviews'
  );
  return (data ?? []).map((item) => ({
    id: item.uuid,
    kind: item.isSigned ? 'signed' : 'unsigned',
    projectName: item.projectName,
    projectNo: item.code,
    department: item.departmentName,
    manager: item.managerName,
    reviewMonth: item.reviewMonth || (item.CreatedAt ? dayjs(item.CreatedAt).format('YYYY-MM') : ''),
    // 空串 / 未返回 → none（无保存内容）
    submissionStatus:
      item.submissionStatus === 'draft' || item.submissionStatus === 'submitted'
        ? item.submissionStatus
        : 'none',
    submittedAt: item.submittedAt ?? undefined,
  }));
};

/**
 * 获取项目毛利复盘的成员工时数列表（A1 工时填报错误勾选用）
 * @param uuid 复盘唯一标识
 */
export const getProjectGrossProfitReviewWorktimesApi = async (
  uuid: string
): Promise<ReviewWorktime[]> => {
  const data: { list?: GrossProfitReviewWorktimeRaw[] } = await request.get(
    `/workbenchApi/projects/project-gross-profit-reviews/${uuid}/worktimes`
  );
  const list = data?.list ?? [];
  return list.map((item) => ({
    // worktimes 接口返回的均为系统按 BI 工时汇总写入的记录
    source: 'system' as const,
    id: item.ID,
    clerkCode: item.clerkCode,
    name: item.name,
    majorName: item.majorName,
    companyName: item.companyName,
    departmentName: item.departmentName ?? '',
    totalHours: item.totalHours,
    workday: item.workday,
  }));
}

/**
 * 按 UUID 获取单条复盘记录（ProjectGrossProfitReview 原样序列化）
 * @param uuid 复盘唯一标识
 */
export const getProjectGrossProfitReviewByUUIDApi = (
  uuid: string
): Promise<GrossProfitReviewDetailRaw> => {
  return request.get(`/workbenchApi/projects/project-gross-profit-reviews/${uuid}`);
}

/**
 * 获取复盘对应项目商机列表（项目编号 + 工程编号）
 * @param uuid 复盘唯一标识
 */
export const getProjectGrossProfitReviewBusinessOpportunitiesApi = async (
  uuid: string
): Promise<GrossProfitReviewBusinessOpportunitiesResp> => {
  const data: GrossProfitReviewBusinessOpportunitiesRespRaw = await request.get(
    `/workbenchApi/projects/project-gross-profit-reviews/${uuid}/business-opportunities`
  );
  return {
    projectOpportunities: Array.isArray(data?.projectOpportunities) ? data.projectOpportunities : [],
    projectOpportunitiesByProjectCode: Array.isArray(data?.projectOpportunitiesByProjectCode)
      ? data.projectOpportunitiesByProjectCode
      : [],
  };
}

/**
 * 获取某条复盘对应项目的 BI 毛利 KPI 快照列表（PLM_PRJ_SHOW_KPI_SAVE，按 savedate 倒序）
 * @param uuid 复盘唯一标识
 */
export const getProjectGrossProfitReviewKpiSavesApi = (
  uuid: string
): Promise<GrossProfitReviewKpiSavesResp> => {
  return request.get(`/workbenchApi/projects/project-gross-profit-reviews/${uuid}/kpi-saves`);
}

/**
 * 提交/暂存复盘请求体：kind 与复盘记录 isSigned 一致，按 kind 携带对应表单内容
 */
export type SubmitProjectGrossProfitReviewBody =
  | { kind: 'signed'; signed: SignedReviewFormValue }
  | { kind: 'unsigned'; unsigned: UnsignedReviewFormValue };

/**
 * 提交项目毛利复盘（服务端按 spec 第 5 节校验，含 A1 工时归属校验）
 * @param uuid 复盘唯一标识
 */
export const submitProjectGrossProfitReviewApi = (
  uuid: string,
  body: SubmitProjectGrossProfitReviewBody
): Promise<unknown> => {
  return request.post(`/workbenchApi/projects/project-gross-profit-reviews/${uuid}/submit`, body);
}

/**
 * 暂存项目毛利复盘（不做必填校验，覆盖式保存最新内容）
 * @param uuid 复盘唯一标识
 */
export const saveProjectGrossProfitReviewDraftApi = (
  uuid: string,
  body: SubmitProjectGrossProfitReviewBody
): Promise<unknown> => {
  return request.post(`/workbenchApi/projects/project-gross-profit-reviews/${uuid}/draft`, body);
}

/** 上次复盘内容（同项目编码上一条已提交记录；无历史提交时 payload 为 null） */
export interface GrossProfitReviewLastSubmissionResp {
  uuid: string;
  status?: string;
  submittedAt?: string;
  payload?: SignedReviewFormValue | UnsignedReviewFormValue | null;
}

/**
 * 获取同项目编码上一条已提交的复盘内容（连续触发复盘时的「上次复盘内容」来源）
 * @param uuid 复盘唯一标识
 */
export const getProjectGrossProfitReviewLastSubmissionApi = (
  uuid: string
): Promise<GrossProfitReviewLastSubmissionResp> => {
  return request.get(`/workbenchApi/projects/project-gross-profit-reviews/${uuid}/last-submission`);
}

/** 当前复盘记录自己的最新保存内容（暂存 / 提交；无保存记录时 payload 为 null） */
export interface GrossProfitReviewSubmissionResp {
  uuid: string;
  kind?: ReviewKind;
  status?: 'draft' | 'submitted';
  submittedAt?: string | null;
  payload?: SignedReviewFormValue | UnsignedReviewFormValue | null;
}

/**
 * 获取当前复盘记录自己的最新保存内容（暂存 / 提交，表单回填用）
 * 与 last-submission 不同：last-submission 取同项目编码「其他」复盘记录的上一条已提交；
 * 本接口取「当前」复盘记录自己的最新一条（draft / submitted 均可）。
 * @param uuid 复盘唯一标识
 */
export const getProjectGrossProfitReviewSubmissionApi = (
  uuid: string
): Promise<GrossProfitReviewSubmissionResp> => {
  return request.get(`/workbenchApi/projects/project-gross-profit-reviews/${uuid}/submission`);
}

/** 项目搜索候选项（未签约复盘「选正确项目 / 关联项目」） */
export interface ProjectSearchOption {
  value: string;
  label: string;
}

/**
 * 项目搜索（按关键字匹配项目编码 / 名称）
 * @param keyword 搜索关键字；空关键字由调用方短路，不发起请求
 */
export const searchProjectsApi = (keyword: string): Promise<ProjectSearchOption[]> => {
  return request.get('/workbenchApi/projects/search', {
    params: { keyword },
  });
}

/**
 * 获取用工信息（A1 工时填报错误「添加员工」候选）
 * 按工号、姓名或邮箱关键字模糊匹配，返回 工号、姓名、邮箱、专业、公司
 * @param keyword 搜索关键字；空关键字由调用方短路，不发起请求
 */
export const searchEmploymentInfosApi = (keyword: string): Promise<EmploymentInfo[]> => {
  return request.get('/workbenchApi/projects/employment-infos', {
    params: { keyword },
  });
}